import { alpha } from '@mui/material/styles';
import { Z_INDEX } from '../../shared/constants/theme';
import { isNativeAppShell } from '../../utils/mobileView';
import { LOGIN_ACCENT } from './loginDarkTheme';

export const AUTH_LIGHT_TEXT = {
  primary: '#111827',
  secondary: '#374151',
  muted: '#4b5563',
  label: '#6b7280',
};

export const authFormPanelSx = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: { xs: '100%', md: '50%' },
  bgcolor: '#fafafa',
  backgroundImage: 'linear-gradient(135deg, rgba(76, 225, 182, 0.04) 0%, rgba(0, 0, 0, 0.01) 100%)',
  p: { xs: 2, sm: 4, md: 5, lg: 7 },
  minHeight: '100vh',
  overflowY: 'auto',
};

export const authFormPanelSxWithGlow = {
  ...authFormPanelSx,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-20%',
    width: '600px',
    height: '600px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(76, 225, 182, 0.08) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-30%',
    left: '-10%',
    width: '500px',
    height: '500px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(76, 225, 182, 0.05) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
};

export function authFormContainerSx(wide = false) {
  return {
    width: '100%',
    px: { xs: 0, sm: 2 },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: 1,
    position: 'relative',
    zIndex: 1,
    maxWidth: { sm: wide ? 620 : 420 },
  };
}

export const freeTrialPrimarySx = (compact = false) => ({
  py: compact ? 1.1 : 1.45,
  minHeight: compact ? 48 : undefined,
  borderRadius: 3,
  textTransform: 'none',
  bgcolor: LOGIN_ACCENT,
  color: '#04120e',
  fontWeight: 900,
  boxShadow: `0 14px 36px ${alpha(LOGIN_ACCENT, 0.32)}`,
  '&:hover': { bgcolor: '#5eead4' },
  '&.Mui-disabled': { bgcolor: 'rgba(76,225,182,0.36)', color: 'rgba(255,255,255,0.48)' },
});

export const freeTrialSecondarySx = (light = false, compact = false) => ({
  py: compact ? 1.1 : 1.45,
  minHeight: compact ? 48 : undefined,
  borderRadius: 3,
  textTransform: 'none',
  color: light ? AUTH_LIGHT_TEXT.primary : '#fff',
  borderColor: light ? 'rgba(17,24,39,0.2)' : 'rgba(255,255,255,0.18)',
  fontWeight: 800,
  '&:hover': { borderColor: LOGIN_ACCENT, bgcolor: 'rgba(76,225,182,0.08)' },
});

export const freeTrialFieldSx = (light = false, compact = false) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 3,
    bgcolor: light ? '#ffffff' : 'rgba(255,255,255,0.045)',
    color: light ? AUTH_LIGHT_TEXT.primary : '#fff',
    minHeight: compact ? 48 : 58,
    '& fieldset': { borderColor: light ? 'rgba(17,24,39,0.16)' : 'rgba(255,255,255,0.15)' },
    '&:hover fieldset': { borderColor: 'rgba(76,225,182,0.45)' },
    '&.Mui-focused fieldset': { borderColor: LOGIN_ACCENT },
    '&.Mui-error fieldset': { borderColor: '#f87171' },
  },
  '& .MuiInputBase-input': {
    color: light ? AUTH_LIGHT_TEXT.primary : '#fff',
    WebkitTextFillColor: light ? AUTH_LIGHT_TEXT.primary : '#fff',
    py: compact ? 1.35 : undefined,
  },
  '& .MuiInputLabel-root': {
    color: light ? AUTH_LIGHT_TEXT.label : 'rgba(255,255,255,0.52)',
    fontSize: compact ? '0.9rem' : undefined,
  },
  '& .MuiInputLabel-root.Mui-focused': { color: LOGIN_ACCENT },
  '& .MuiFormHelperText-root': { color: '#fca5a5', ml: 0.5 },
  '& .MuiSelect-icon': { color: light ? AUTH_LIGHT_TEXT.label : 'rgba(255,255,255,0.65)' },
});

export const authOverlayFieldShellSx = {
  '& .MuiFormControl-root': { overflow: 'visible' },
  '& .MuiAutocomplete-root': { overflow: 'visible' },
  '& .MuiSelect-root': { overflow: 'visible' },
};

/** Autocomplete lists inside auth dialogs / mobile shell (Capacitor-safe). */
export function getAuthAutocompletePaperSx(light = false) {
  return light
    ? {
        bgcolor: '#fff',
        border: '1px solid rgba(17,24,39,0.14)',
        borderRadius: '10px',
        mt: 0.5,
        boxShadow: '0 12px 32px rgba(15,23,42,0.12)',
        '& .MuiAutocomplete-option': { color: '#111827', fontSize: '0.875rem', py: 1 },
        '& .MuiAutocomplete-option[aria-selected="true"]': { bgcolor: alpha(LOGIN_ACCENT, 0.14) },
        '& .MuiAutocomplete-option.Mui-focused': { bgcolor: alpha(LOGIN_ACCENT, 0.08) },
      }
    : {
        bgcolor: '#141414',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: '10px',
        mt: 0.5,
        boxShadow: '0 16px 48px rgba(0,0,0,0.55)',
        '& .MuiAutocomplete-option': { color: 'rgba(255,255,255,0.92)', fontSize: '0.875rem', py: 1 },
        '& .MuiAutocomplete-option[aria-selected="true"]': { bgcolor: alpha(LOGIN_ACCENT, 0.14) },
        '& .MuiAutocomplete-option.Mui-focused': { bgcolor: alpha(LOGIN_ACCENT, 0.1) },
      };
}

export function getAuthAutocompleteFieldSx(light = false, compact = false) {
  const field = freeTrialFieldSx(light, compact);
  const outlinedInputSx = field['& .MuiOutlinedInput-root'];

  return {
    ...field,
    width: '100%',
    '& .MuiAutocomplete-inputRoot': outlinedInputSx,
    '& .MuiAutocomplete-popupIndicator': { color: light ? AUTH_LIGHT_TEXT.label : 'rgba(255,255,255,0.55)' },
    '& .MuiAutocomplete-clearIcon': { color: light ? '#9ca3af' : 'rgba(255,255,255,0.45)' },
    '& .MuiAutocomplete-input': { fontSize: compact ? '0.9rem' : '0.95rem', minWidth: 0 },
  };
}

/** Autocomplete lists inside auth dialogs / mobile shell (Capacitor-safe). */
export function getAuthAutocompleteSlotProps(light = false, { paperSx = {} } = {}) {
  const mergedPaperSx = {
    maxHeight: 'min(280px, 45vh)',
    ...getAuthAutocompletePaperSx(light),
    ...paperSx,
  };

  return {
    popper: {
      disablePortal: isNativeAppShell(),
      placement: 'bottom-start',
      sx: { zIndex: Z_INDEX.MAXIMUM },
    },
    paper: { sx: mergedPaperSx },
    listbox: { sx: { maxHeight: 'min(280px, 45vh)' } },
  };
}

export function freeTrialSelectMenuProps(light = false) {
  const menuPaperBg = light ? '#ffffff' : '#141414';
  const menuBorder = light ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)';
  const menuItemColor = light ? '#111827' : 'rgba(255,255,255,0.9)';
  const menuHover = light ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)';

  return {
    disableScrollLock: true,
    sx: { zIndex: Z_INDEX.MAXIMUM },
    PaperProps: {
      sx: {
        zIndex: Z_INDEX.MAXIMUM,
        bgcolor: menuPaperBg,
        border: `1px solid ${menuBorder}`,
        borderRadius: 2,
        maxHeight: 'min(280px, 45vh)',
        boxShadow: light
          ? '0 8px 24px rgba(15,23,42,0.12)'
          : '0 12px 32px rgba(0,0,0,0.45)',
        '& .MuiMenuItem-root': {
          color: menuItemColor,
          fontSize: '0.875rem',
          '&:hover': { bgcolor: menuHover },
          '&.Mui-selected': {
            bgcolor: alpha(LOGIN_ACCENT, 0.14),
            color: light ? '#047857' : LOGIN_ACCENT,
            '&:hover': { bgcolor: alpha(LOGIN_ACCENT, 0.18) },
          },
        },
      },
    },
    MenuListProps: { dense: true },
  };
}

export const freeTrialCardSx = (error, light = false, compact = false) => ({
  border: `1px solid ${error ? '#f87171' : light ? 'rgba(17,24,39,0.14)' : 'rgba(255,255,255,0.14)'}`,
  borderRadius: 3,
  bgcolor: error ? 'rgba(239,68,68,0.08)' : light ? '#ffffff' : 'rgba(255,255,255,0.035)',
  p: compact ? 1.35 : 2,
});

export const freeTrialMiniLabelSx = (light = false) => ({
  color: light ? AUTH_LIGHT_TEXT.secondary : 'rgba(255,255,255,0.58)',
  fontSize: '0.75rem',
  fontWeight: 900,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  mb: 1.2,
  display: 'block',
});

export const freeTrialRadioSx = (light = false) => ({
  color: light ? 'rgba(17,24,39,0.42)' : 'rgba(255,255,255,0.36)',
  '&.Mui-checked': { color: LOGIN_ACCENT },
});

export const freeTrialCheckboxSx = (light = false) => ({
  color: light ? 'rgba(17,24,39,0.42)' : 'rgba(255,255,255,0.36)',
  '&.Mui-checked': { color: LOGIN_ACCENT },
});
